import {
  Layers,
  ArrowRight,
  Bot,
  Database,
  FlaskConical,
  GitBranch,
  BookOpen,
  Shield,
  Sparkles,
  FolderPlus,
  MousePointerClick,
  Upload,
  FileOutput,
  Brain,
  ShieldCheck,
} from "lucide-react";
import { AGENTS } from "../agents";
import GreenTendrils from "./GreenTendrils";

interface Props {
  onGetStarted: () => void;
}

/* GreenTendrils is now imported from ./GreenTendrils */

const ICON_MAP: Record<string, typeof Database> = {
  Database,
  BookOpen,
  FlaskConical,
  GitBranch,
  ShieldCheck,
};

export default function LandingPage({ onGetStarted }: Props) {
  const activeCount = AGENTS.filter((a) => a.status === "active").length;
  const totalCount = AGENTS.length;

  return (
    <div className="overflow-y-auto custom-scrollbar h-full bg-surface-500">
      {/* ── Hero Section ─────────────────────────────── */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Dot grid */}
        <div className="absolute inset-0 dot-grid" />
        {/* Glow orbs */}
        <div className="glow-orb glow-orb-1" />
        <div className="glow-orb glow-orb-2" />
        <div className="glow-orb glow-orb-3" />
        {/* Flowing tendrils */}
        <GreenTendrils />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Left: Title & CTA */}
          <div className="flex-1 space-y-8 animate-fade-in">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-500/30 text-brand-400 text-sm font-medium"
              style={{ background: "rgba(134,188,37,0.08)" }}
            >
              <Sparkles size={14} />
              SI-Powered Data Agents
            </div>

            <h1 className="text-6xl md:text-7xl font-black leading-[0.95] tracking-tight">
              <span className="gradient-text-green">ZeroTouchData</span>
              <span className="text-white/40 font-extralight">.si</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/70 font-light leading-relaxed max-w-lg">
              Your data pipeline, fully automated.
              <br />
              <span className="text-white font-medium">LLM-powered agents</span>
              , human-supervised results.
            </p>

            <div className="flex items-center gap-4 pt-2">
              <button
                onClick={onGetStarted}
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-base text-white transition-all duration-300"
                style={{
                  background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                  boxShadow:
                    "0 0 30px rgba(134,188,37,0.3), 0 4px 20px rgba(0,0,0,0.3)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 0 50px rgba(134,188,37,0.5), 0 8px 30px rgba(0,0,0,0.4)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 0 30px rgba(134,188,37,0.3), 0 4px 20px rgba(0,0,0,0.3)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                Get Started
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
              <span className="text-xs text-white/30">
                {activeCount} active &middot; {totalCount - activeCount} coming
                soon
              </span>
            </div>
          </div>

          {/* Right: Feature hero card (like the reference) */}
          <div className="flex-shrink-0 w-full max-w-md animate-slide-up">
            <div className="glass-hero-card rounded-2xl p-8 space-y-6">
              {/* Abstract green graphic area */}
              <div
                className="relative h-48 rounded-xl overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #0E0E18, #1C1C2A)",
                }}
              >
                <svg
                  viewBox="0 0 400 200"
                  className="absolute inset-0 w-full h-full"
                >
                  <defs>
                    <linearGradient
                      id="hg1"
                      x1="0%"
                      y1="100%"
                      x2="100%"
                      y2="0%"
                    >
                      <stop offset="0%" stopColor="#86BC25" stopOpacity="0.7" />
                      <stop
                        offset="50%"
                        stopColor="#A7D200"
                        stopOpacity="0.4"
                      />
                      <stop
                        offset="100%"
                        stopColor="#C4D600"
                        stopOpacity="0.1"
                      />
                    </linearGradient>
                    <filter id="hglow">
                      <feGaussianBlur stdDeviation="4" />
                    </filter>
                  </defs>
                  <path
                    d="M0,180 C80,120 160,60 240,100 S360,40 400,80"
                    stroke="url(#hg1)"
                    strokeWidth="3"
                    fill="none"
                    filter="url(#hglow)"
                  >
                    <animate
                      attributeName="d"
                      values="M0,180 C80,120 160,60 240,100 S360,40 400,80;M0,160 C80,80 160,120 240,60 S360,100 400,50;M0,180 C80,120 160,60 240,100 S360,40 400,80"
                      dur="6s"
                      repeatCount="indefinite"
                    />
                  </path>
                  <path
                    d="M0,140 C100,100 200,160 300,80 S380,120 400,110"
                    stroke="#86BC25"
                    strokeWidth="2"
                    fill="none"
                    opacity="0.5"
                    filter="url(#hglow)"
                  >
                    <animate
                      attributeName="d"
                      values="M0,140 C100,100 200,160 300,80 S380,120 400,110;M0,120 C100,160 200,100 300,140 S380,70 400,90;M0,140 C100,100 200,160 300,80 S380,120 400,110"
                      dur="8s"
                      repeatCount="indefinite"
                    />
                  </path>
                  <path
                    d="M0,100 C60,60 180,140 280,50 S380,90 400,60"
                    stroke="#6CC24A"
                    strokeWidth="1.5"
                    fill="none"
                    opacity="0.3"
                    filter="url(#hglow)"
                  >
                    <animate
                      attributeName="d"
                      values="M0,100 C60,60 180,140 280,50 S380,90 400,60;M0,80 C60,120 180,80 280,100 S380,50 400,80;M0,100 C60,60 180,140 280,50 S380,90 400,60"
                      dur="10s"
                      repeatCount="indefinite"
                    />
                  </path>
                </svg>
                {/* Radial green glow center */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="w-24 h-24 rounded-full animate-glow-pulse"
                    style={{
                      background:
                        "radial-gradient(circle, rgba(134,188,37,0.25), transparent 70%)",
                    }}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-white mb-1">
                  Zero Touch Data
                </h3>
                <p className="text-sm text-white/50 leading-relaxed">
                  Transform raw transactional data into actionable reporting
                  with SI agents — profiling, masking, modeling, and deployment
                  — LLM-driven with human oversight at every step.
                </p>
              </div>

              <button
                onClick={onGetStarted}
                className="w-full py-3 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2 transition-all duration-300"
                style={{
                  background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                }}
              >
                Explore Agents <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 space-y-24 pb-12">
        {/* ── How It Works ────────────────────────────── */}
        <section className="animate-slide-up">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-white">How It Works</h2>
            <p className="text-sm text-white/40 mt-2">
              From raw data to deployment-ready reporting
            </p>
          </div>
          <div className="flex flex-col md:flex-row items-stretch gap-0 max-w-4xl mx-auto stagger-in">
            {[
              {
                step: "1",
                title: "Create Project",
                desc: "Organize your work into dedicated project spaces",
                Icon: FolderPlus,
                color: "#86BC25",
              },
              {
                step: "2",
                title: "Select Agent",
                desc: "Choose a specialized SI agent from the catalog",
                Icon: MousePointerClick,
                color: "#A7D200",
              },
              {
                step: "3",
                title: "Provide Input",
                desc: "Upload files, connect databases, or paste data",
                Icon: Upload,
                color: "#6CC24A",
              },
              {
                step: "4",
                title: "Get Results",
                desc: "AI generates artifacts, models, and deployable code",
                Icon: FileOutput,
                color: "#C4D600",
              },
            ].map((ws, i) => (
              <div key={ws.step} className="flex items-center flex-1">
                <div className="flex-1 glass-card rounded-2xl p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="step-number">{ws.step}</div>
                  </div>
                  <div
                    className="w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center"
                    style={{ background: `${ws.color}15` }}
                  >
                    <ws.Icon size={20} style={{ color: ws.color }} />
                  </div>
                  <p className="text-sm font-semibold text-white">{ws.title}</p>
                  <p className="text-xs text-white/40 mt-1.5 leading-relaxed">
                    {ws.desc}
                  </p>
                </div>
                {i < 3 && <div className="step-connector hidden md:block" />}
              </div>
            ))}
          </div>
        </section>

        {/* ── Agent Catalog Preview ───────────────────── */}
        <section>
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-white">Agent Catalog</h2>
            <p className="text-sm text-white/40 mt-2">
              Specialized SI agents for every data workflow
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto stagger-in">
            {AGENTS.map((agent) => {
              const Icon = ICON_MAP[agent.icon] || Bot;
              const isActive = agent.status === "active";

              return (
                <div
                  key={agent.id}
                  className={`agent-card-v2 rounded-2xl p-6 ${isActive ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                  style={
                    { "--agent-color": agent.color } as React.CSSProperties
                  }
                  onClick={isActive ? onGetStarted : undefined}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ background: `${agent.color}18` }}
                    >
                      <Icon size={22} style={{ color: agent.color }} />
                    </div>
                    <div className="flex items-center gap-1.5">
                      {agent.llmEnabled && (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold bg-purple-500/10 border border-purple-500/20 text-purple-400">
                          <Brain size={10} /> LLM
                        </span>
                      )}
                      {isActive ? (
                        <span
                          className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"
                          style={{
                            background: `${agent.color}18`,
                            color: agent.color,
                          }}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full animate-pulse"
                            style={{ background: agent.color }}
                          />
                          Active
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-white/5 text-white/30 uppercase tracking-wider">
                          Coming Soon
                        </span>
                      )}
                    </div>
                  </div>

                  <h3
                    className={`font-bold text-base ${isActive ? "text-white" : "text-white/40"}`}
                  >
                    {agent.name}
                  </h3>
                  <span className="text-[11px] text-white/60 uppercase tracking-wider font-semibold">
                    {agent.category}
                  </span>
                  <p
                    className={`text-sm mt-2 leading-relaxed ${isActive ? "text-white/75" : "text-white/45"}`}
                  >
                    {agent.description}
                  </p>

                  {/* Output tags with agent color */}
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {agent.outputs.slice(0, 4).map((o) => (
                      <span
                        key={o.id}
                        className="px-2 py-0.5 rounded text-[11px] font-medium"
                        style={{
                          background: isActive
                            ? `${agent.color}12`
                            : "rgba(255,255,255,0.04)",
                          color: isActive
                            ? agent.color
                            : "rgba(255,255,255,0.3)",
                        }}
                      >
                        {o.label}
                      </span>
                    ))}
                    {agent.outputs.length > 4 && (
                      <span className="px-2 py-0.5 rounded text-[11px] text-white/20">
                        +{agent.outputs.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Application Capabilities ───────────────────── */}
        <section className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-white">
              Application Capabilities
            </h2>
            <p className="text-sm text-white/40 mt-2">
              Enterprise-grade features powered by Claude
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 stagger-in">
            {[
              {
                icon: Database,
                label: "Multi-Source Data",
                desc: "CSV, Excel, JSON, Parquet, Snowflake, Postgres, MySQL, Oracle",
                color: "#86BC25",
              },
              {
                icon: Bot,
                label: "SI-Powered Analysis",
                desc: "Claude Opus 4 & Sonnet 4 for deep understanding",
                color: "#A7D200",
              },
              {
                icon: Shield,
                label: "Data Governance",
                desc: "PII detection, sensitivity mapping, masking policies",
                color: "#6CC24A",
              },
              {
                icon: GitBranch,
                label: "Version Control",
                desc: "Project-based organization with session history",
                color: "#0076A8",
              },
              {
                icon: Layers,
                label: "Multi-Platform Deploy",
                desc: "Snowflake, Oracle, SQL Server, PostgreSQL, MySQL, BigQuery",
                color: "#00A3E0",
              },
              {
                icon: BookOpen,
                label: "Auto Documentation",
                desc: "Word, PDF, HTML documentation with full model details",
                color: "#C4D600",
              },
            ].map((cap) => (
              <div
                key={cap.label}
                className="glass-card rounded-2xl p-5 flex items-start gap-4"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${cap.color}15` }}
                >
                  <cap.icon size={18} style={{ color: cap.color }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">
                    {cap.label}
                  </p>
                  <p className="text-xs text-white/35 mt-1 leading-relaxed">
                    {cap.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="pb-8" />
      </div>
    </div>
  );
}
